import { Navigate } from 'react-router-dom';

import routes from '../routes/router';
import Logger from '../utils/logger';
import request from './request';
import { UserSession } from './types';
import { setAcadlyTimeOffset } from '../utils/datetime';

export default function registerInterceptors({
  token,
  cluster,
  universitySlug,
  courseId,
  setError,
  setSession,
}: {
  token: string | null;
  cluster: string | null;
  universitySlug: string | null;
  courseId: MongoId | null;
  setError: (error: string) => void;
  setSession: (session: UserSession | null) => void;
}) {
  request.interceptors.request.use(function (config) {
    const headers = config.headers;

    if (!config.baseURL) {
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (cluster) {
        config.baseURL = cluster;
      }

      if (headers.univSlug || universitySlug) {
        headers.univSlug = headers.univSlug || universitySlug || '';
      }

      if (headers.courseId || courseId) {
        headers.courseId = headers.courseId || courseId;
      }
    }

    if (headers.Authorization) {
      headers.appClient = 'ms-ppt-add-in';
      headers.appVersion = process.env.REACT_APP_VERSION;

      const { platform, host, version } = Office.context.diagnostics;

      headers['X-ms-ppt-os'] = `${host}=${platform}`;
      headers['X-ms-ppt-version'] = version;
    }

    return config;
  });

  request.interceptors.response.use(
    (response) => {
      const { acadlytime } = response.headers;
      if (!acadlytime) return response;

      setAcadlyTimeOffset(+acadlytime);

      return response;
    },
    (error) => {
      Logger.error(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const message =
          error.response.data.message ||
          'Some error occured, please try after sometime. If the issue persists, please contact us at support@acadly.com';
        setError(message);

        if (error.response.status === 401) {
          setSession(null);
          Navigate({ to: routes.auth.url(), replace: true });
          setError("You've been logged out. Please log in again.");
        } else {
          Logger.debug({
            message,
            request: {
              status: error.response.status,
              data: error.response.data,
            },
          });
        }
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        const message = `Our servers didn't respond. Please try after some time.`;
        setError(message);
      } else {
        // Something happened in setting up the request that triggered an Error
        const message =
          'Some error occured, please try after sometime. If the issue persists, please contact us at support@acadly.com';
        setError(message);
        Logger.error(error);
      }

      // forward error
      return Promise.reject(error);
    }
  );
}
